import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import clsx from "clsx";

import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import { green } from "@mui/material/colors";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import IconButton from "@mui/material/IconButton";
import MoreVert from "@mui/icons-material/MoreVert";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import MoodIcon from "@mui/icons-material/Mood";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import ClearIcon from "@mui/icons-material/Clear";
import MicIcon from "@mui/icons-material/Mic";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import PollIcon from "@mui/icons-material/Poll";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ViewListIcon from "@mui/icons-material/ViewList";
import PixIcon from "@mui/icons-material/Payments";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LinkIcon from "@mui/icons-material/Link";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import QuotedMediaThumb from "../QuotedMediaThumb";
import quotedPreview from "../../helpers/quotedPreview";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import ProductCatalogDialog from "../ProductCatalogDialog";


const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: theme.palette.background.default,
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.background.paper,
    display: "flex",
    borderRadius: 24,
    border: `1px solid ${theme.palette.divider}`,
    flex: 1,
    position: "relative",
  },

  messageInputWrapperPrivate: {
    padding: 6,
    marginRight: 7,
    background: "#FFFDE7",
    display: "flex",
    borderRadius: 24,
    border: "1px dashed #F9A825",
    flex: 1,
    position: "relative",
  },

  privateActiveIcon: {
    color: "#F9A825",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
    color: theme.palette.text.primary,
  },

  sendMessageIcons: {
    color: theme.palette.text.secondary,
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    paddingRight: 8,
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginPreviewLine: {
    marginTop: 2,
    fontSize: "0.9em",
    opacity: 0.85,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    whiteSpace: "normal",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  dropOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: `${theme.palette.primary.main}1F`,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    pointerEvents: "none",
  },

  dropOverlayText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 16,
  },

  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: theme.palette.background.paper,
    padding: "2px",
    border: `1px solid ${theme.palette.divider}`,
    left: 0,
    width: "100%",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        color: theme.palette.text.primary,
        "&:hover": {
          background: theme.palette.action.hover,
          cursor: "pointer",
        },
      },
    },
  },
}));

const MessageInput = ({ ticketStatus, contact, whatsappId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [mediaCaption, setMediaCaption] = useState("");
  const [viewOnce, setViewOnce] = useState(false);
  const mediaBlobUrlsRef = useRef([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [quickDialogOpen, setQuickDialogOpen] = useState(false);
  const [quickDialogSearch, setQuickDialogSearch] = useState("");
  const [quickDialogAnswers, setQuickDialogAnswers] = useState([]);
  const quickDialogTimer = useRef(null);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduledList, setScheduledList] = useState([]);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const [privateMode, setPrivateMode] = useState(false);
  const [draggingOver, setDraggingOver] = useState(false);
  const dragCounter = useRef(0);
  const [agentsTyping, setAgentsTyping] = useState([]);
  const [contactTyping, setContactTyping] = useState(false);
  const contactTypingTimerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const socketRef = useRef(null);
  const nativeRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const ticketStatusRef = useRef(ticketStatus);
  useEffect(() => { ticketStatusRef.current = ticketStatus; }, [ticketStatus]);

  // Socket para indicador de digitação
  useEffect(() => {
    if (!ticketId || !user?.name) return;
    const socket = openSocket();
    socketRef.current = socket;
    socket.emit("joinChatBox", `${ticketId}`);

    const handleAgentTyping = ({ ticketId: tid, userName, isTyping }) => {
      if (String(tid) !== String(ticketId) || userName === user.name) return;
      setAgentsTyping(prev =>
        isTyping ? (prev.includes(userName) ? prev : [...prev, userName]) : prev.filter(n => n !== userName)
      );
    };
    const handleContactTyping = ({ isTyping }) => {
      setContactTyping(isTyping);
      clearTimeout(contactTypingTimerRef.current);
      if (isTyping) {
        // Auto-apaga após 8s sem atualização (segurança contra eventos perdidos)
        contactTypingTimerRef.current = setTimeout(() => setContactTyping(false), 8000);
      }
    };
    socket.on("agentTyping", handleAgentTyping);
    socket.on("contactTyping", handleContactTyping);

    return () => {
      socket.off("agentTyping", handleAgentTyping);
      socket.off("contactTyping", handleContactTyping);
      clearTimeout(contactTypingTimerRef.current);
      socket.emit("leaveChatBox", `${ticketId}`);
      if (isTypingRef.current) {
        socket.emit("agentTyping", { ticketId, userName: user.name, isTyping: false });
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [ticketId, user]);

  useEffect(() => {
    mediaBlobUrlsRef.current.forEach(url => url && URL.revokeObjectURL(url));
    mediaBlobUrlsRef.current = medias.map(file =>
      file.type.startsWith("image/") || file.type.startsWith("video/")
        ? URL.createObjectURL(file)
        : null
    );
    return () => {
      mediaBlobUrlsRef.current.forEach(url => url && URL.revokeObjectURL(url));
    };
  }, [medias]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    if (!ticketId) return;
    const saved = localStorage.getItem(`draft:${ticketId}`);
    if (saved) setInputMessage(saved);
    if (inputRef.current) inputRef.current.focus();
    return () => {
      setInputMessage(prev => {
        if (ticketId) {
          // Só persiste rascunho se o ticket ainda estava aberto ao sair.
          // Tickets fechados/resolvidos não devem restaurar o texto.
          if (prev.trim() && ticketStatusRef.current === "open") {
            localStorage.setItem(`draft:${ticketId}`, prev);
          } else {
            localStorage.removeItem(`draft:${ticketId}`);
          }
        }
        return "";
      });
      setShowEmoji(false);
      setTypeBar(false);
      setQuickAnswer([]);
      setPrivateMode(false);
      setMedias([]);
      setMediaCaption("");
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);

    // Emite "digitando" para outros agentes no mesmo ticket
    if (socketRef.current && user?.name) {
      if (!isTypingRef.current) {
        isTypingRef.current = true;
        socketRef.current.emit("agentTyping", { ticketId, userName: user.name, isTyping: true });
      }
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        socketRef.current?.emit("agentTyping", { ticketId, userName: user.name, isTyping: false });
      }, 3000);
    }
  };

  const applyVariables = (text) => {
    return text
      .replace(/\{\{nome\}\}/gi, contact?.name || "")
      .replace(/\{\{numero\}\}/gi, contact?.number || "")
      .replace(/\{\{atendente\}\}/gi, user?.name || "");
  };

  const handleQuickAnswersClick = (value) => {
    const processed = applyVariables(value);
    setTypeBar(false);
    setInputMessage(processed);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    if (e?.preventDefault) e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => formData.append("medias", media));
    if (mediaCaption.trim()) {
      formData.append("body", mediaCaption.trim());
    }
    if (viewOnce) {
      formData.append("isViewOnce", "true");
    }
    if (replyingMessage?.id) {
      formData.append("quotedMsgId", replyingMessage.id);
    }

    try {
      await api.post(`/messages/${ticketId}`, formData, {
        onUploadProgress: (evt) => {
          if (evt.total && evt.loaded < evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setLoadingProgress(pct);
          }
        },
      });
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setLoadingProgress(0);
    setMedias([]);
    setMediaCaption("");
    setViewOnce(false);
    setReplyingMessage(null);
  };

  const loadScheduled = async () => {
    try {
      const { data } = await api.get(`/messages/${ticketId}/scheduled`);
      setScheduledList(data);
    } catch {}
  };

  const handleOpenSchedule = () => {
    setScheduleOpen(true);
    loadScheduled();
  };

  const handleSchedule = async () => {
    if (!inputMessage.trim() || !scheduledAt) { toast.error("Escreva a mensagem e escolha a data/hora."); return; }
    try {
      await api.post(`/messages/${ticketId}/scheduled`, { body: inputMessage.trim(), scheduledAt });
      toast.success("Mensagem agendada.");
      localStorage.removeItem(`draft:${ticketId}`);
      setInputMessage("");
      setScheduledAt("");
      loadScheduled();
    } catch (err) { toast.error("Erro ao agendar mensagem."); }
  };

  const handleDeleteScheduled = async (id) => {
    try { await api.delete(`/messages/scheduled/${id}`); loadScheduled(); } catch {}
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: privateMode
        ? inputMessage.trim()
        : signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
      isPrivate: privateMode,
    };

    // Limpa input imediatamente — não bloqueia o usuário enquanto WPPConnect processa
    localStorage.removeItem(`draft:${ticketId}`);
    setInputMessage("");
    setShowEmoji(false);
    setReplyingMessage(null);
    setPrivateMode(false);
    setTimeout(() => inputRef.current?.focus(), 0);

    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Seu navegador não suporta gravação de áudio (requer HTTPS).");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      nativeRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      nativeRecorderRef.current.addEventListener("dataavailable", e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      });
      nativeRecorderRef.current.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      nativeRecorderRef.current = null;
      audioChunksRef.current = [];
      const msg =
        err?.name === "NotAllowedError"
          ? "Permissão de microfone negada. Verifique as configurações do navegador."
          : err?.name === "NotFoundError"
          ? "Nenhum microfone encontrado. Conecte um microfone ao computador e tente novamente."
          : err?.name === "NotReadableError"
          ? "Microfone em uso por outro aplicativo. Feche outros apps e tente novamente."
          : err?.message || "Erro ao iniciar gravação de áudio.";
      toast.error(msg, { autoClose: 8000 });
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      if (!nativeRecorderRef.current) {
        throw new Error("Nenhuma gravação em andamento.");
      }
      await new Promise(resolve => {
        nativeRecorderRef.current.addEventListener("stop", resolve, { once: true });
        nativeRecorderRef.current.stop();
      });
      // Release microphone
      nativeRecorderRef.current.stream.getTracks().forEach(t => t.stop());

      const chunks = audioChunksRef.current;
      if (chunks.length === 0 || chunks.reduce((s, c) => s + c.size, 0) < 1000) {
        toast.warn("Áudio muito curto, tente novamente.");
        nativeRecorderRef.current = null;
        audioChunksRef.current = [];
        setRecording(false);
        setLoading(false);
        return;
      }

      const mimeType = nativeRecorderRef.current.mimeType || "audio/webm";
      const ext = mimeType.includes("ogg") ? "ogg" : "webm";
      const blob = new Blob(chunks, { type: mimeType });
      const filename = `${new Date().getTime()}.${ext}`;

      const formData = new FormData();
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      nativeRecorderRef.current = null;
      audioChunksRef.current = [];
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      nativeRecorderRef.current = null;
      audioChunksRef.current = [];
      toastError(err);
    }
    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = () => {
    if (nativeRecorderRef.current) {
      try {
        nativeRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        if (nativeRecorderRef.current.state !== "inactive") nativeRecorderRef.current.stop();
      } catch (_) {}
      nativeRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setRecording(false);
  };

  const handleSendSticker = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const formData = new FormData();
    formData.append("medias", file);
    try {
      await api.post(`/messages/${ticketId}/sticker`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Figurinha enviada!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSendLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não disponível neste navegador.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          await api.post(`/messages/${ticketId}/location`, {
            lat: coords.latitude,
            lng: coords.longitude,
            title: "Minha localização",
          });
          toast.success("Localização enviada!");
        } catch (err) {
          toastError(err);
        }
      },
      () => toast.error("Não foi possível obter sua localização.")
    );
  };

  const [pollOpen, setPollOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);

  const handleSendPoll = async () => {
    const opts = pollOptions.map(o => o.trim()).filter(Boolean);
    if (!pollQuestion.trim() || opts.length < 2) {
      toast.error("Enquete precisa de pergunta e pelo menos 2 opções.");
      return;
    }
    try {
      await api.post(`/messages/${ticketId}/poll`, { question: pollQuestion.trim(), options: opts });
      toast.success("Enquete enviada!");
      setPollOpen(false);
      setPollQuestion("");
      setPollOptions(["", ""]);
    } catch (err) {
      toastError(err);
    }
  };

  const [pixOpen, setPixOpen] = useState(false);
  const [pixData, setPixData] = useState({ key: "", keyType: "phone", name: "", city: "" });
  const [pixPresets, setPixPresets] = useState(() => { try { return JSON.parse(localStorage.getItem("pixPresets") || "[]"); } catch { return []; } });
  const [pixPresetName, setPixPresetName] = useState("");
  const [pixSaveOpen, setPixSaveOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [listTitle, setListTitle] = useState("");
  const [listButtonText, setListButtonText] = useState("");
  const [listSections, setListSections] = useState([{ sectionTitle: "", rows: [{ title: "", description: "" }] }]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [vcardOpen, setVcardOpen] = useState(false);
  const [extraMenuAnchor, setExtraMenuAnchor] = useState(null);
  const [vcardSearch, setVcardSearch] = useState("");
  const [vcardContacts, setVcardContacts] = useState([]);
  const [vcardLoading, setVcardLoading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  const validatePixKey = (key, keyType) => {
    if (!key) return false;
    const k = key.trim();
    if (keyType === "cpf") {
      if (!/^\d{11}$/.test(k)) return false;
      if (/^(\d)\1{10}$/.test(k)) return false;
      let s = 0;
      for (let i = 0; i < 9; i++) s += parseInt(k[i]) * (10 - i);
      let r = (s * 10) % 11; if (r === 10 || r === 11) r = 0;
      if (r !== parseInt(k[9])) return false;
      s = 0;
      for (let i = 0; i < 10; i++) s += parseInt(k[i]) * (11 - i);
      r = (s * 10) % 11; if (r === 10 || r === 11) r = 0;
      return r === parseInt(k[10]);
    }
    if (keyType === "cnpj") {
      if (!/^\d{14}$/.test(k)) return false;
      if (/^(\d)\1{13}$/.test(k)) return false;
      const calc = (s, w) => { let t = 0; for (let i = 0; i < s.length; i++) t += parseInt(s[i]) * w[i]; const r = t % 11; return r < 2 ? 0 : 11 - r; };
      const w1 = [5,4,3,2,9,8,7,6,5,4,3,2]; const w2 = [6,5,4,3,2,9,8,7,6,5,4,3,2];
      return calc(k.slice(0,12), w1) === parseInt(k[12]) && calc(k.slice(0,13), w2) === parseInt(k[13]);
    }
    if (keyType === "phone") return /^\+55\d{10,11}$/.test(k);
    if (keyType === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(k);
    if (keyType === "random") return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(k);
    return true;
  };

  const handleSendPix = async () => {
    if (!pixData.key || !pixData.name || !pixData.city) {
      toast.error("Preencha todos os campos Pix.");
      return;
    }
    if (!validatePixKey(pixData.key.trim(), pixData.keyType)) {
      const hints = { cpf: "CPF inválido (11 dígitos)", cnpj: "CNPJ inválido (14 dígitos)", phone: "Telefone inválido (ex: +5531999999999)", email: "E-mail inválido", random: "Chave aleatória inválida (formato UUID)" };
      toast.error(hints[pixData.keyType] || "Chave PIX inválida.");
      return;
    }
    try {
      await api.post(`/messages/${ticketId}/pix`, pixData);
      toast.success("Chave Pix enviada!");
      setPixOpen(false);
      setPixData({ key: "", keyType: "phone", name: "", city: "" });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSavePixPreset = () => {
    const name = pixPresetName.trim();
    if (!name) return;
    const updated = [...pixPresets.filter(p => p.name !== name), { name, ...pixData }];
    localStorage.setItem("pixPresets", JSON.stringify(updated));
    setPixPresets(updated);
    setPixPresetName("");
    setPixSaveOpen(false);
    toast.success(`Predefinição "${name}" salva!`);
  };

  const handleDeletePixPreset = (name) => {
    const updated = pixPresets.filter(p => p.name !== name);
    localStorage.setItem("pixPresets", JSON.stringify(updated));
    setPixPresets(updated);
  };

  const handleSendList = async () => {
    if (!listButtonText.trim()) {
      toast.error("Texto do botão é obrigatório.");
      return;
    }
    const sections = listSections
      .filter(s => s.rows.some(r => r.title.trim()))
      .map(s => ({
        title: s.sectionTitle.trim(),
        rows: s.rows.filter(r => r.title.trim()).map(r => ({ title: r.title.trim(), description: r.description.trim() })),
      }));
    if (!sections.length) {
      toast.error("Adicione pelo menos uma opção.");
      return;
    }
    try {
      await api.post(`/messages/${ticketId}/list`, {
        title: listTitle.trim() || undefined,
        buttonText: listButtonText.trim(),
        sections,
      });
      toast.success("Lista enviada!");
      setListOpen(false);
      setListTitle("");
      setListButtonText("");
      setListSections([{ sectionTitle: "", rows: [{ title: "", description: "" }] }]);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickDialog = () => {
    setQuickDialogSearch("");
    setQuickDialogAnswers([]);
    setQuickDialogOpen(true);
    api.get("/quickAnswers/", { params: { searchParam: "" } })
      .then(({ data }) => setQuickDialogAnswers(data.quickAnswers || []))
      .catch(() => {});
  };

  const handleQuickDialogSearch = (e) => {
    const val = e.target.value;
    setQuickDialogSearch(val);
    clearTimeout(quickDialogTimer.current);
    quickDialogTimer.current = setTimeout(() => {
      api.get("/quickAnswers/", { params: { searchParam: val } })
        .then(({ data }) => setQuickDialogAnswers(data.quickAnswers || []))
        .catch(() => {});
    }, 300);
  };

  const handleQuickDialogSelect = (message) => {
    const processed = applyVariables(message);
    setQuickDialogOpen(false);
    setInputMessage(processed);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSendLinkPreview = async () => {
    if (!linkUrl.trim()) { toast.error("URL é obrigatória."); return; }
    try {
      await api.post(`/messages/${ticketId}/link-preview`, { url: linkUrl.trim(), text: linkText.trim() || undefined });
      toast.success("Link com preview enviado!");
      setLinkOpen(false);
      setLinkUrl("");
      setLinkText("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    const { icon, text, thumb } = quotedPreview(message);
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer} style={{ alignItems: "center" }}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody} style={{ flex: 1, minWidth: 0 }}>
            <span className={classes.messageContactName}>
              {message.fromMe ? "Você" : (message.contact?.name || "Cliente")}
            </span>
            <div className={classes.replyginPreviewLine}>
              {icon ? <span style={{ marginRight: 4 }}>{icon}</span> : null}
              {text}
            </div>
          </div>
          {thumb && message?.mediaUrl ? (
            <QuotedMediaThumb mediaUrl={message.mediaUrl} size={40} marginLeft={0} />
          ) : null}
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setDraggingOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingOver(false);
    dragCounter.current = 0;
    if (ticketStatus !== "open") return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setMedias(files);
    }
  };

  if (medias.length > 0) {
    const visibleThumbs = medias.slice(0, 4);
    const extra = medias.length - 4;
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconButton
              size="small"
              aria-label="cancel-upload"
              component="span"
              onClick={() => { setMedias([]); setMediaCaption(""); }}
            >
              <CancelIcon className={classes.sendMessageIcons} />
            </IconButton>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flex: 1, flexWrap: "wrap" }}>
              {visibleThumbs.map((file, i) => {
                const url = mediaBlobUrlsRef.current[i];
                const isImage = file.type.startsWith("image/");
                return (
                  <div
                    key={i}
                    style={{
                      width: 48, height: 48, borderRadius: 4, overflow: "hidden",
                      background: "#e0e0e0", flexShrink: 0, display: "flex",
                      alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {isImage && url ? (
                      <img src={url} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <AttachFileIcon style={{ fontSize: 22, color: "#666" }} />
                    )}
                  </div>
                );
              })}
              {extra > 0 && (
                <span style={{ fontSize: 12, color: "#666" }}>+{extra}</span>
              )}
              <span style={{ fontSize: 12, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                {medias[0].name}{medias.length > 1 ? ` +${medias.length - 1}` : ""}
              </span>
            </div>
          </div>
          {loading ? (
            <div style={{ padding: "4px 8px" }}>
              {loadingProgress > 0 && loadingProgress < 100 ? (
                <div>
                  <LinearProgress variant="determinate" value={loadingProgress} style={{ borderRadius: 4, marginBottom: 4 }} />
                  <div style={{ textAlign: "center", fontSize: 11, color: "#888" }}>Enviando… {loadingProgress}%</div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "4px 0" }}>
                  <CircularProgress size={24} className={classes.circleLoading} style={{ position: "static", margin: 0 }} />
                  {loadingProgress >= 100 && <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Processando pelo WhatsApp…</div>}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <InputBase
                  style={{ flex: 1, background: "rgba(0,0,0,0.05)", borderRadius: 20, padding: "4px 14px", fontSize: 14 }}
                  placeholder="Adicionar legenda..."
                  value={mediaCaption}
                  onChange={e => setMediaCaption(e.target.value)}
                  onKeyPress={e => {
                    if (e.key === "Enter" && !e.shiftKey) handleUploadMedia(e);
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={viewOnce}
                      onChange={e => setViewOnce(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption" style={{ fontSize: 11 }}>Única vez</Typography>}
                  style={{ margin: 0, flexShrink: 0 }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                aria-label="send-upload"
                component="span"
                onClick={handleUploadMedia}
                disabled={loading}
              >
                <SendIcon className={classes.sendMessageIcons} />
              </IconButton>
              </div>
            </div>
          )}
        </div>
      </Paper>
    );
  }
  else {
    return (
      <Paper
        square
        elevation={0}
        className={classes.mainWrapper}
        style={{ position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {draggingOver && (
          <div className={classes.dropOverlay}>
            <span className={classes.dropOverlayText}>Solte para enviar o arquivo</span>
          </div>
        )}
        {contactTyping && (
          <div style={{ padding: "2px 12px", fontSize: 12, color: "#4CAF50", fontStyle: "italic" }}>
            Cliente está digitando...
          </div>
        )}
        {agentsTyping.length > 0 && (
          <div style={{ padding: "2px 12px", fontSize: 12, color: "gray", fontStyle: "italic" }}>
            {agentsTyping.length === 1
              ? `${agentsTyping[0]} está digitando...`
              : `${agentsTyping.join(", ")} estão digitando...`}
          </div>
        )}
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        <div className={classes.newMessageBox}>
          <Box sx={{ display: { xs: "none", sm: "none", md: "contents" } }}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || recording || ticketStatus !== "open"}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    data={emojiData}
                    perLine={16}
                    previewPosition="none"
                    skinTonePosition="none"
                    onEmojiSelect={handleAddEmoji}
                    locale="pt"
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || recording || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <Tooltip title="Respostas rápidas">
              <IconButton
                aria-label="quickAnswers"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={handleOpenQuickDialog}
              >
                <FlashOnIcon className={classes.sendMessageIcons} />
              </IconButton>
            </Tooltip>

            {/* Ocultos — acessíveis pelo menu "+" */}
            <input id="sticker-upload" type="file" accept="image/*" style={{ display: "none" }} onChange={e => { handleSendSticker(e); setExtraMenuAnchor(null); }} disabled={loading || recording || ticketStatus !== "open"} />

            <Tooltip title="Mais ações">
              <span>
                <IconButton
                  aria-label="moreActions"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={e => setExtraMenuAnchor(e.currentTarget)}
                  style={extraMenuAnchor ? { color: theme.palette.primary.main } : {}}
                >
                  <AddCircleOutlineIcon className={classes.sendMessageIcons} />
                </IconButton>
              </span>
            </Tooltip>

            <Popover
              open={Boolean(extraMenuAnchor)}
              anchorEl={extraMenuAnchor}
              onClose={() => setExtraMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{ style: { padding: "8px 4px" } }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                {[
                  { label: "Figurinha", icon: <EmojiEmotionsIcon />, action: () => document.getElementById("sticker-upload").click() },
                  { label: "Localização", icon: <LocationOnIcon />, action: handleSendLocation },
                  { label: "Enquete", icon: <PollIcon />, action: () => setPollOpen(true) },
                  { label: "Pix", icon: <PixIcon />, action: () => setPixOpen(true) },
                  { label: "Lista", icon: <ViewListIcon />, action: () => setListOpen(true) },
                  ...(whatsappId ? [
                    { label: "Catálogo", icon: <StorefrontIcon />, action: () => setCatalogOpen(true) },
                    { label: "Contato", icon: <ContactPhoneIcon />, action: () => { setVcardSearch(""); setVcardContacts([]); setVcardOpen(true); } },
                    { label: "Link", icon: <LinkIcon />, action: () => { setLinkUrl(""); setLinkText(""); setLinkOpen(true); } },
                  ] : []),
                  { label: "Agendar", icon: <AlarmAddIcon />, action: handleOpenSchedule },
                ].map(({ label, icon, action }) => (
                  <ButtonBase
                    key={label}
                    onClick={() => { action(); setExtraMenuAnchor(null); }}
                    style={{ flexDirection: "column", padding: "10px 14px", borderRadius: 8, minWidth: 72 }}
                  >
                    {React.cloneElement(icon, { style: { fontSize: 22, color: theme.palette.primary.main, marginBottom: 4 } })}
                    <Typography variant="caption" style={{ fontSize: 11, color: "#555", lineHeight: 1 }}>{label}</Typography>
                  </ButtonBase>
                ))}
              </div>
            </Popover>

            <Tooltip title={privateMode ? "Modo aviso interno ativo" : "Enviar aviso interno"}>
              <IconButton
                aria-label="privateNote"
                component="span"
                disabled={loading || recording || ticketStatus !== "open"}
                onClick={() => setPrivateMode(prev => !prev)}
                className={privateMode ? classes.privateActiveIcon : ""}
              >
                <VisibilityOffIcon />
              </IconButton>
            </Tooltip>
            <FormControlLabel
              style={{ marginRight: 7, color: "gray" }}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          </Box>
          <Box sx={{ display: { xs: "contents", sm: "contents", md: "none" } }}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || recording || ticketStatus !== "open"}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || recording || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || recording || ticketStatus !== "open"}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  style={{ marginRight: 7, color: "gray" }}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      onChange={e => {
                        setSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </MenuItem>
            </Menu>
          </Box>
          <div className={privateMode ? classes.messageInputWrapperPrivate : classes.messageInputWrapper}>
            <InputBase
              inputRef={inputRef}
              className={classes.messageInput}
              placeholder={
                privateMode
                  ? "Aviso interno (não enviado ao cliente)..."
                  : ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.messageQuickAnswersWrapperItem}
                      key={index}
                    >
                      <a
                        onMouseDown={e => {
                          e.preventDefault();
                          handleQuickAnswersClick(value.message);
                        }}
                      >
                        {`${value.shortcut} - ${value.message}`}
                      </a>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <>
              <IconButton
                aria-label="sendMessage"
                component="span"
                onClick={handleSendMessage}
                disabled={loading}
              >
                <SendIcon className={classes.sendMessageIcons} />
              </IconButton>
            </>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <IconButton
              aria-label="showRecorder"
              component="span"
              disabled={loading || ticketStatus !== "open"}
              onClick={handleStartRecording}
            >
              <MicIcon className={classes.sendMessageIcons} />
            </IconButton>
          )}
        </div>
        <Dialog open={quickDialogOpen} onClose={() => setQuickDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Respostas Rápidas</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar por atalho ou texto..."
              value={quickDialogSearch}
              onChange={handleQuickDialogSearch}
              style={{ marginBottom: 8 }}
            />
            <List dense style={{ maxHeight: 320, overflowY: "auto" }}>
              {quickDialogAnswers.length === 0 && (
                <ListItem><ListItemText secondary="Nenhuma resposta encontrada." /></ListItem>
              )}
              {quickDialogAnswers.map((qa) => (
                <ListItemButton key={qa.id} onClick={() => handleQuickDialogSelect(qa.message)}>
                  <Chip label={qa.shortcut} size="small" color="primary" style={{ marginRight: 8, flexShrink: 0 }} />
                  <ListItemText
                    primary={qa.message}
                    primaryTypographyProps={{ noWrap: true, style: { overflow: "hidden", textOverflow: "ellipsis" } }}
                  />
                </ListItemButton>
              ))}
            </List>
          </DialogContent>
        </Dialog>

        {/* Enquete */}
        <Dialog open={pollOpen} onClose={() => setPollOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Criar enquete</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="Pergunta"
              fullWidth
              variant="outlined"
              margin="dense"
              value={pollQuestion}
              onChange={e => setPollQuestion(e.target.value)}
            />
            {pollOptions.map((opt, i) => (
              <TextField
                key={i}
                label={`Opção ${i + 1}`}
                fullWidth
                variant="outlined"
                margin="dense"
                value={opt}
                onChange={e => {
                  const copy = [...pollOptions];
                  copy[i] = e.target.value;
                  setPollOptions(copy);
                }}
              />
            ))}
            <Button size="small" onClick={() => setPollOptions(p => [...p, ""])} style={{ marginTop: 4 }}>
              + Adicionar opção
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPollOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="primary" onClick={handleSendPoll}>Enviar</Button>
          </DialogActions>
        </Dialog>

        {/* Pix */}
        <Dialog open={pixOpen} onClose={() => { setPixOpen(false); setPixSaveOpen(false); }} maxWidth="xs" fullWidth>
          <DialogTitle style={{ paddingBottom: 4 }}>
            Enviar chave Pix
            {pixPresets.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value=""
                onChange={e => { if (e.target.value) setPixData({ ...pixPresets.find(p => p.name === e.target.value) }); }}
                style={{ marginLeft: 12, fontSize: 13, minWidth: 140 }}
              >
                <MenuItem value="" disabled>Carregar predefinição</MenuItem>
                {pixPresets.map(p => (
                  <MenuItem key={p.name} value={p.name} style={{ fontSize: 13 }}>
                    {p.name}
                    <span
                      onClick={ev => { ev.stopPropagation(); handleDeletePixPreset(p.name); }}
                      style={{ marginLeft: 8, color: "#e53935", cursor: "pointer", fontWeight: "bold" }}
                      title="Excluir predefinição"
                    > ×</span>
                  </MenuItem>
                ))}
              </Select>
            )}
          </DialogTitle>
          <DialogContent>
            <Select
              fullWidth
              size="small"
              value={pixData.keyType}
              onChange={e => setPixData(p => ({ ...p, keyType: e.target.value }))}
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              <MenuItem value="phone">Telefone</MenuItem>
              <MenuItem value="cpf">CPF</MenuItem>
              <MenuItem value="cnpj">CNPJ</MenuItem>
              <MenuItem value="email">E-mail</MenuItem>
              <MenuItem value="random">Chave aleatória</MenuItem>
            </Select>
            <TextField
              label={{"phone":"Telefone (+5531999999999)","cpf":"CPF (somente números)","cnpj":"CNPJ (somente números)","email":"E-mail","random":"Chave aleatória (UUID)"}[pixData.keyType] || "Chave Pix"}
              autoFocus
              fullWidth
              variant="outlined"
              margin="dense"
              value={pixData.key}
              onChange={e => setPixData(p => ({ ...p, key: e.target.value.trim() }))}
              inputProps={{ inputMode: ["cpf","cnpj"].includes(pixData.keyType) ? "numeric" : "text" }}
            />
            <TextField
              label="Nome do recebedor"
              fullWidth
              variant="outlined"
              margin="dense"
              value={pixData.name}
              onChange={e => setPixData(p => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="Cidade"
              fullWidth
              variant="outlined"
              margin="dense"
              value={pixData.city}
              onChange={e => setPixData(p => ({ ...p, city: e.target.value }))}
            />
            {pixSaveOpen && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                <TextField
                  label="Nome da predefinição"
                  size="small"
                  fullWidth
                  autoFocus
                  value={pixPresetName}
                  onChange={e => setPixPresetName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSavePixPreset(); if (e.key === "Escape") setPixSaveOpen(false); }}
                />
                <Button size="small" variant="contained" color="primary" onClick={handleSavePixPreset}>OK</Button>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPixOpen(false)} style={{ marginRight: "auto" }}>Cancelar</Button>
            <Button
              size="small"
              onClick={() => setPixSaveOpen(s => !s)}
              startIcon={<span>💾</span>}
            >
              Salvar
            </Button>
            <Button variant="contained" color="primary" onClick={handleSendPix}>Enviar</Button>
          </DialogActions>
        </Dialog>
        {whatsappId && (
          <ProductCatalogDialog
            open={catalogOpen}
            onClose={() => setCatalogOpen(false)}
            whatsappId={whatsappId}
            contactNumber={contact?.number}
            ticketId={ticketId}
            onProductSelect={(text, imageFile) => {
              if (imageFile) {
                setMedias([imageFile]);
                setMediaCaption(text);
              } else {
                setInputMessage(text);
              }
              setCatalogOpen(false);
            }}
          />
        )}
        {/* Diálogo enviar contato como vCard */}
        <Dialog open={vcardOpen} onClose={() => setVcardOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Enviar contato</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Buscar contato..."
              value={vcardSearch}
              onChange={async e => {
                const val = e.target.value;
                setVcardSearch(val);
                if (val.length < 2) { setVcardContacts([]); return; }
                setVcardLoading(true);
                try {
                  const { data } = await api.get("/contacts", { params: { searchParam: val, pageNumber: 1 } });
                  setVcardContacts(data.contacts || []);
                } catch { setVcardContacts([]); }
                finally { setVcardLoading(false); }
              }}
              InputProps={{ endAdornment: vcardLoading ? <CircularProgress size={16} /> : null }}
              style={{ marginBottom: 8, marginTop: 4 }}
            />
            <List dense>
              {vcardContacts.slice(0, 10).map(c => (
                <ListItemButton
                  key={c.id}
                  onClick={async () => {
                    if (!whatsappId || !contact?.number) return;
                    const chatId = `${contact.number}@c.us`;
                    const contactId = `${c.number}@c.us`;
                    try {
                      await api.post(`/whatsapp/${whatsappId}/send-vcard`, { to: chatId, contactId, name: c.name });
                    } catch (err) { toastError(err); }
                    setVcardOpen(false);
                  }}
                >
                  <ListItemText primary={c.name} secondary={c.number} />
                </ListItemButton>
              ))}
              {vcardContacts.length === 0 && vcardSearch.length >= 2 && !vcardLoading && (
                <ListItem><ListItemText secondary="Nenhum contato encontrado" /></ListItem>
              )}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVcardOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Lista interativa */}
        <Dialog open={listOpen} onClose={() => setListOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Enviar lista interativa</DialogTitle>
          <DialogContent>
            <TextField
              label="Título (opcional)"
              fullWidth
              variant="outlined"
              margin="dense"
              value={listTitle}
              onChange={e => setListTitle(e.target.value)}
            />
            <TextField
              autoFocus
              label="Texto do botão *"
              fullWidth
              variant="outlined"
              margin="dense"
              value={listButtonText}
              onChange={e => setListButtonText(e.target.value)}
              placeholder="ex: Ver opções"
            />
            {listSections.map((sec, si) => (
              <div key={si} style={{ marginTop: 12, padding: 8, border: "1px solid #e0e0e0", borderRadius: 4 }}>
                <TextField
                  label={`Título da seção ${si + 1} (opcional)`}
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="dense"
                  value={sec.sectionTitle}
                  onChange={e => {
                    const copy = listSections.map((s, i) => i === si ? { ...s, sectionTitle: e.target.value } : s);
                    setListSections(copy);
                  }}
                />
                {sec.rows.map((row, ri) => (
                  <div key={ri} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}>
                    <TextField
                      label={`Opção ${ri + 1}`}
                      variant="outlined"
                      size="small"
                      value={row.title}
                      onChange={e => {
                        const copy = listSections.map((s, i) => i === si ? {
                          ...s, rows: s.rows.map((r, j) => j === ri ? { ...r, title: e.target.value } : r)
                        } : s);
                        setListSections(copy);
                      }}
                      style={{ flex: 2 }}
                    />
                    <TextField
                      label="Descrição"
                      variant="outlined"
                      size="small"
                      value={row.description}
                      onChange={e => {
                        const copy = listSections.map((s, i) => i === si ? {
                          ...s, rows: s.rows.map((r, j) => j === ri ? { ...r, description: e.target.value } : r)
                        } : s);
                        setListSections(copy);
                      }}
                      style={{ flex: 3 }}
                    />
                  </div>
                ))}
                <Button size="small" onClick={() => {
                  const copy = listSections.map((s, i) => i === si ? { ...s, rows: [...s.rows, { title: "", description: "" }] } : s);
                  setListSections(copy);
                }} style={{ marginTop: 4 }}>
                  + Opção
                </Button>
              </div>
            ))}
            <Button size="small" onClick={() => setListSections(prev => [...prev, { sectionTitle: "", rows: [{ title: "", description: "" }] }])} style={{ marginTop: 8 }}>
              + Seção
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setListOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="primary" onClick={handleSendList}>Enviar</Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de mensagem agendada */}
        <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle><AlarmAddIcon sx={{ mr: 1, verticalAlign: "middle" }} />Agendar mensagem</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
            <TextField
              label="Mensagem"
              fullWidth
              multiline
              minRows={3}
              value={inputMessage}
              onChange={e => setInputMessage(e.target.value)}
              variant="outlined"
            />
            <TextField
              label="Data e hora do envio"
              type="datetime-local"
              fullWidth
              value={scheduledAt}
              onChange={e => setScheduledAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            />
            {scheduledList.length > 0 && (
              <>
                <div style={{ fontSize: "0.8rem", color: "gray", marginTop: 8 }}>Mensagens agendadas para este ticket:</div>
                <List dense disablePadding>
                  {scheduledList.map(sm => (
                    <ListItem key={sm.id} divider secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => handleDeleteScheduled(sm.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }>
                      <ListItemText
                        primary={sm.body}
                        secondary={new Date(sm.scheduledAt).toLocaleString("pt-BR")}
                        primaryTypographyProps={{ style: { fontSize: "0.82rem" } }}
                        secondaryTypographyProps={{ style: { fontSize: "0.75rem" } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleOpen(false)}>Fechar</Button>
            <Button onClick={handleSchedule} variant="contained" color="primary" disabled={!inputMessage.trim() || !scheduledAt}>
              Agendar
            </Button>
          </DialogActions>
        </Dialog>
        {/* Link Preview */}
        <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Enviar link com preview</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              label="URL"
              fullWidth
              variant="outlined"
              margin="dense"
              placeholder="https://exemplo.com"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSendLinkPreview()}
            />
            <TextField
              label="Texto (opcional)"
              fullWidth
              variant="outlined"
              margin="dense"
              value={linkText}
              onChange={e => setLinkText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkOpen(false)}>Cancelar</Button>
            <Button variant="contained" color="primary" onClick={handleSendLinkPreview}>Enviar</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    );
  }
};

export default MessageInput;
